import { z } from "zod";

export type Tone = "neutral" | "info" | "attention" | "success" | "danger";
export type MilestoneStatus = "completed" | "current" | "skipped" | "conditional" | "pending";
export type WaitingOn = "customer" | "after_sales" | "warehouse" | "technician" | "quality" | "carrier" | "" | null;

export const stateSchema = z.object({
  code: z.string(),
  milestone: z.string(),
  customer_label: z.string(),
  admin_label: z.string(),
  label: z.string(),
  waiting_on: z.string().nullable().optional(),
  tone: z.enum(["neutral", "info", "attention", "success", "danger"]),
  threshold_hours: z.number().nullable().optional(),
  terminal: z.boolean(),
  version: z.number(),
});

export type WorkflowState = z.infer<typeof stateSchema>;

export type Milestone = {
  key: string;
  order: number;
  customer_label: string;
  admin_label: string;
  mobile_macro: string;
  conditional: boolean;
  status: MilestoneStatus;
};

export type ActionDescriptor = {
  code: string;
  label: string;
  intent: "primary" | "secondary" | "danger";
  required_fields: string[];
  requires_confirmation: boolean;
  result_state: string;
  result_label: string;
};

export type Attachment = {
  id: string;
  category: string;
  module_key?: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  temp_url?: string;
  visible_to_customer: boolean;
  uploaded_at: string;
};

export type TimelineEvent = {
  id: string;
  status_name: string;
  state_code?: string;
  action_type: string;
  action_code?: string;
  action_note: string;
  operator_name: string;
  operator_role: string;
  visible_to_customer: boolean;
  created_at: string;
};

export type OrderItem = {
  id: string;
  order_no: string;
  sn: string;
  fault_desc: string;
  extra_note?: string;
  receiver_name: string;
  receiver_phone: string;
  receiver_address: string;
  current_status: string;
  state_code: string;
  milestone_key: string;
  waiting_on: WaitingOn;
  state_version: number;
  owner_id?: string;
  owner_name?: string;
  priority?: string;
  created_at: string;
  updated_at: string;
  next_action_due_at?: string | null;
  sla_risk?: SlaRisk | null;
  state?: WorkflowState;
};

export type QuoteItem = {
  id?: string;
  item_type?: string;
  item_name: string;
  description?: string;
  quantity: number;
  unit_price: number;
  amount?: number;
};

export type Quote = {
  id: string;
  version_no: number;
  status: string;
  total_amount: number;
  currency?: string;
  valid_until?: string | null;
  warranty_text?: string;
  estimated_days?: number | null;
  items: QuoteItem[];
  note?: string;
  created_at?: string;
  customer_decision?: string;
  customer_decided_at?: string | null;
};

export type Shipment = {
  id: string;
  direction: "INBOUND" | "OUTBOUND";
  carrier: string;
  tracking_no: string;
  status: string;
  shipped_at?: string | null;
  delivered_at?: string | null;
  exception_note?: string;
  nodes?: Array<{ status: string; description: string; time: string; location?: string }>;
};

export type SlaRisk = {
  level: "warning" | "danger";
  title: string;
  waiting_hours: number;
  threshold_hours: number;
  action_hint: string;
  acknowledged_at?: string | null;
};

export type OrderDetail = {
  item: OrderItem;
  state: WorkflowState;
  milestones: Milestone[];
  available_actions: ActionDescriptor[];
  timeline: TimelineEvent[];
  inspection_records: Array<Record<string, unknown>>;
  quote: Quote | null;
  repair_record: Record<string, unknown> | null;
  quality_check: Record<string, unknown> | null;
  shipments: Shipment[];
  logistics: Record<string, unknown> | null;
  logistics_tracking: Record<string, unknown>;
  attachments: Attachment[];
  sla_risk: SlaRisk | null;
  assignment: {
    owner_id: string;
    owner_name: string;
    priority: string;
    history: Array<Record<string, unknown>>;
  };
};

export type ListResult<T> = {
  items: T[];
  total: number;
  limit: number;
  offset: number;
};

export const waitingLabels: Record<string, string> = {
  customer: "等待客户",
  after_sales: "等待售后",
  warehouse: "等待仓储",
  technician: "等待维修",
  quality: "等待质检",
  carrier: "运输途中",
  system: "系统处理中",
  "": "无需等待",
};

export const macroMilestones = ["报修", "寄回", "检修", "报价", "维修", "回寄", "完成"];

export function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function bytesLabel(value = 0) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 ** 2).toFixed(1)} MB`;
}
